import { ReactNode } from 'react'
import { randomId } from '@shared/utils/random'

/* ---------------------------------- Types --------------------------------- */

/** Names a toast in analytics; `type` is its breakdown, the rest ride along on every event about it. Never ids or user text. */
export interface ToastAnalytics {
  type: string
  [property: string]: unknown
}

/** Why a toast left: unset when its own body dismissed it, usually after a click. */
export type ToastDismissReason = 'dismiss button' | 'timeout' | 'emptied' | 'evicted'

type DismissListener = (toast: ToastItem, reason: ToastDismissReason | undefined) => void

export interface ToastItem {
  id: string
  title?: string
  headerExtra?: ReactNode
  description?: ReactNode
  notchDescription?: ReactNode
  notchRow?: ReactNode
  /** `description` paints its own icon and title, so the notch shouldn't add them again. */
  descriptionHasHeader?: boolean
  icon?: ReactNode
  fill?: string
  roundness?: number
  duration?: number | null
  /** Exempt from the notch's duration cap, so it stays up until dismissed. */
  persistent?: boolean
  styles?: { title?: string; description?: string; badge?: string }
  onDismiss?: () => void
  onHeaderClick?: () => void
  exiting?: boolean
  instanceId: string
  autoExpandDelayMs?: number
  autoCollapseDelayMs?: number
  keepExpandedOnRefresh?: boolean
  stableHeaderKey?: string
  priority?: number
  analytics?: ToastAnalytics
}

export interface ToastShowOptions {
  id?: string
  title?: string
  headerExtra?: ReactNode
  description?: ReactNode
  notchDescription?: ReactNode
  notchRow?: ReactNode
  /** `description` paints its own icon and title, so the notch shouldn't add them again. */
  descriptionHasHeader?: boolean
  icon?: ReactNode
  fill?: string
  roundness?: number
  duration?: number | null
  persistent?: boolean
  styles?: { title?: string; description?: string; badge?: string }
  autoExpandDelayMs?: number
  autoCollapseDelayMs?: number
  keepExpandedOnRefresh?: boolean
  stableHeaderKey?: string
  priority?: number
  onDismiss?: () => void
  onHeaderClick?: () => void
  analytics?: ToastAnalytics
}

/* ---------------------------------- Store --------------------------------- */

type Listener = (toasts: Array<ToastItem>) => void

const EXIT_DURATION = 600

let idCounter = 0
const generateId = () => `${++idCounter}-${Date.now().toString(36)}-${randomId().slice(0, 8)}`

const store = {
  toasts: [] as Array<ToastItem>,
  listeners: new Set<Listener>(),
  dismissListeners: new Set<DismissListener>(),

  emit() {
    for (const fn of this.listeners) fn(this.toasts)
  },

  update(fn: (prev: Array<ToastItem>) => Array<ToastItem>) {
    this.toasts = fn(this.toasts)
    this.emit()
  },
}

/* -------------------------------- Suppression ----------------------------- */

let suppressed = true
const UNSUPPRESSABLE_IDS = new Set<string>()

/* --------------------------------- Routing -------------------------------- */

let isMiniNotched = false
let routesAllToNotch = false

/** A toast that brings its own notch presentation, a body under the header or a single row. */
export function hasNotchContent(toast: ToastItem): boolean {
  return Boolean(toast.notchDescription || toast.notchRow)
}

/** The notch redesign shows every toast in the notch; otherwise a notched Mini takes only those with notch content. */
export function isShownInViewport(toast: ToastItem, miniNotched: boolean, allToNotch = routesAllToNotch): boolean {
  return !allToNotch && (!miniNotched || !hasNotchContent(toast))
}

/* -------------------------------- Public API ------------------------------ */

const MAX_VISIBLE = 3

function enforceVisibleCap() {
  const active = store.toasts.filter((t) => !t.exiting && isShownInViewport(t, isMiniNotched))
  if (active.length <= MAX_VISIBLE) {
    return
  }

  // Evict by importance, then age. `active` is already in insertion order
  // (oldest first), so a stable sort on priority alone drops the
  // lowest-priority oldest toasts first. This keeps an actionable toast
  // (e.g. the staged-action toast, updated in place at an old position)
  // from being dismissed just because informational chat-complete toasts
  // piled up on top of it.
  const evicting = active
    .map((t, index) => ({ t, index }))
    .sort((a, b) => (a.t.priority ?? 0) - (b.t.priority ?? 0) || a.index - b.index)
    .slice(0, active.length - MAX_VISIBLE)
  for (const { t } of evicting) {
    toastManager.dismiss(t.id, 'evicted')
  }
}

export const toastManager = {
  setSuppressed(value: boolean) {
    suppressed = value
  },

  setMiniNotched(value: boolean) {
    if (value === isMiniNotched) {
      return
    }
    isMiniNotched = value
    // Un-notching returns the notch's toasts to the viewport, which can push it over the cap.
    enforceVisibleCap()
  },

  setRoutesAllToNotch(value: boolean) {
    if (value === routesAllToNotch) {
      return
    }
    routesAllToNotch = value
    enforceVisibleCap()
  },

  show(options: ToastShowOptions): string {
    const id = options.id ?? generateId()
    if (suppressed && !UNSUPPRESSABLE_IDS.has(id)) return id

    const instanceId = generateId()
    const item: ToastItem = { ...options, id, instanceId }

    const existing = store.toasts.find((t) => t.id === id && !t.exiting)
    if (existing) {
      store.update((prev) => prev.map((t) => (t.id === id ? { ...t, ...item, instanceId } : t)))
    } else {
      store.update((prev) => [...prev.filter((t) => t.id !== id), item])
    }

    enforceVisibleCap()

    return id
  },

  /**
   * Re-time a visible toast in place. The viewport keys dismiss timers on
   * duration, so this restarts the auto-dismiss countdown at the new value
   * without re-showing the toast (no instanceId churn, no pill refresh).
   */
  setDuration(id: string, duration: number | null) {
    const item = store.toasts.find((t) => t.id === id && !t.exiting)
    if (!item || item.duration === duration) return
    store.update((prev) => prev.map((t) => (t.id === id ? { ...t, duration } : t)))
  },

  dismiss(id: string, reason?: ToastDismissReason) {
    const item = store.toasts.find((t) => t.id === id)
    if (!item || item.exiting) return

    item.onDismiss?.()
    for (const fn of store.dismissListeners) fn(item, reason)
    const instanceId = item.instanceId
    store.update((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)))
    setTimeout(() => {
      store.update((prev) => prev.filter((t) => !(t.id === id && t.instanceId === instanceId)))
    }, EXIT_DURATION)
  },

  clear() {
    store.update(() => [])
  },

  /** Called once per dismissal, with why, before the toast starts exiting. */
  onDismissed(fn: DismissListener) {
    store.dismissListeners.add(fn)
    return () => {
      store.dismissListeners.delete(fn)
    }
  },

  subscribe(fn: Listener) {
    store.listeners.add(fn)
    return () => store.listeners.delete(fn)
  },

  getToasts(): Array<ToastItem> {
    return store.toasts
  },
}
